/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, Inject, Input, OnChanges, signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

const ACTIVITY_SERIES = [
  { key: 'status_count', label: 'Status updates', tone: 'status' },
  { key: 'article_count', label: 'Articles', tone: 'article' },
  { key: 'post_count', label: 'Forum posts', tone: 'post' },
  { key: 'comment_count', label: 'Comments', tone: 'comment' },
];

const PLATFORM_BADGES = [
  { key: 'functional', label: 'Functional' },
  { key: 'usability', label: 'Usability' },
  { key: 'localization', label: 'Localization' },
  { key: 'security', label: 'Security' },
  { key: 'load', label: 'Load' },
  // Not in uTestV2's platform_badges yet (UT-1807): shows Unrated until the API sends it.
  { key: 'accessibility', label: 'Accessibility' },
];

const VISIBLE_COMMUNITY_BADGES = 6;
const CHART_TICKS = 4;

interface ActivityInterval {
  label: string;
  title: string;
  totals: Record<string, number>;
  // Each month's bar: segment heights as a percent of the y-axis maximum.
  bars: { label: string; segments: { tone: string; percent: number }[] }[];
  ticks: number[];
}

// Rounds the y-axis up so every tick is a whole number.
function axisMax(max: number): number {
  return Math.max(CHART_TICKS, Math.ceil(max / CHART_TICKS) * CHART_TICKS);
}

const sentenceCase = (text: string): string => {
  const lower = (text || '').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

// Builds the chart from user.site_stats.intervals, the same data the AngularJS stat-line-chart used.
export function toActivityInterval(interval: any): ActivityInterval {
  const totals = Object.fromEntries(ACTIVITY_SERIES.map((s) => [s.key, 0]));
  const sums = interval.data.map((month) => ACTIVITY_SERIES.reduce((sum, s) => {
    const value = month.values?.[s.key] || 0;
    totals[s.key] += value;
    return sum + value;
  }, 0));
  const max = axisMax(Math.max(0, ...sums));
  return {
    label: interval.label,
    // The API sends "LAST 12 MONTHS"; shown as "Last 12 months".
    title: sentenceCase(interval.label),
    totals,
    bars: interval.data.map((month) => ({
      label: month.label,
      segments: ACTIVITY_SERIES.map((s) => ({ tone: s.tone, percent: ((month.values?.[s.key] || 0) * 100) / max })),
    })),
    ticks: Array.from({ length: CHART_TICKS + 1 }, (_, i) => (max * (CHART_TICKS - i)) / CHART_TICKS),
  };
}

// The Overview tab: About and community activity on the left; badges and platform stats on the right.
@Component({
  selector: 'ut-community-profile-overview',
  templateUrl: './community-profile-overview.component.html',
  styleUrls: ['./community-profile-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityProfileOverviewComponent implements OnChanges {
  @Input({ required: true }) user: any;

  readonly series = ACTIVITY_SERIES;
  readonly visibleBadges = VISIBLE_COMMUNITY_BADGES;

  // Rich text from the tester app's editor is HTML; older summaries are markdown.
  aboutHtml: string | null = null;
  aboutMarkdown: SafeHtml | null = null;
  intervals: ActivityInterval[] = [];
  interval = signal<ActivityInterval | null>(null);
  platformBadges: { label: string; level: string }[] = [];
  communityBadges: { name: string; icon: string; points: number }[] = [];
  showAllBadges = signal(false);

  constructor(
    @Inject('$filter') private $filter: any,
    @Inject('GamificationService') private GamificationService: any,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnChanges(): void {
    const summary = this.user.professional_summary;
    const hasSummary = summary && summary !== 'undefined';
    this.aboutHtml = hasSummary && /^\s*</.test(summary) ? summary : null;
    this.aboutMarkdown = hasSummary && !this.aboutHtml
      ? this.sanitizer.bypassSecurityTrustHtml(this.$filter('uMarkdown')(summary))
      : null;

    this.intervals = (this.user.site_stats?.intervals || []).map(toActivityInterval);
    this.interval.set(this.intervals[0] || null);

    const levels = this.user.platform_badges;
    this.platformBadges = levels && !this.user.is_employee
      ? PLATFORM_BADGES.map((b) => ({ label: b.label, level: levels[b.key] || 'unrated' }))
      : [];

    const byId = this.GamificationService.badgesById || {};
    this.communityBadges = (this.user.badges || [])
      .filter((b) => byId[b.badge_type])
      .map((b) => ({ name: byId[b.badge_type].name, icon: byId[b.badge_type].icon, points: b.points }))
      .sort((a, b) => b.points - a.points);
  }

  get stats(): any {
    return this.user.site_stats || {};
  }

  get hasPlatformStats(): boolean {
    return !!(this.stats.joined_at || this.stats.last_login_at || this.stats.last_commented_at);
  }

  selectInterval(label: string): void {
    this.interval.set(this.intervals.find((i) => i.label === label) || null);
  }

  chartLabel(interval: ActivityInterval): string {
    const totals = this.series.map((s) => `${interval.totals[s.key]} ${s.label.toLowerCase()}`).join(', ');
    return `Community activity, ${interval.label}: ${totals}`;
  }
}
